import type * as lazyFunctions from './lazyFunctions';
import { markFunction } from 'owa-performance';
import { LazyBootModule, LazyAction } from 'owa-bundling-light';
import type { MailboxInfo } from 'owa-client-types';
import type { OnTabUnLoad, OnTabLoaded } from './appCaching.interface';
import type { Context } from '@microsoft/teams-js/dist/esm/packages/teams-js/dts/public/app/app';
import type { HeadersWithoutIterator } from 'owa-service/lib/RequestOptions';

const lazyModule = new LazyBootModule<typeof lazyFunctions>(
    () => import(/* webpackChunkName: "AppBootMetaOs" */ './lazyFunctions'),
    { name: 'AppBootMetaOs' }
);

export const lazyInitializeMetaOsAppSdkProxy: LazyAction<
    () => Promise<void>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m =>
    markFunction(
        m.lazyInitializeMetaOsAppSdk.importAndExecute,
        'MetaOSPlatformAppBootImportAndExecuteScript'
    )
);

export const lazyGetOwaAuthTokenFromMetaOsHubProxy: LazyAction<
    (
        headers?: HeadersWithoutIterator | undefined,
        _mailboxInfo?: MailboxInfo | undefined
    ) => Promise<string | undefined>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyGetOwaAuthTokenFromMetaOsHub.importAndExecute);

export const lazyInitializeMetaOsCapabilitiesProxy: LazyAction<
    () => Promise<void>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyInitializeMetaOsCapabilities.importAndExecute);

export const lazyGetContextProxy: LazyAction<
    () => Promise<Context>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyGetContext.importAndExecute);

export const lazyRegisterOnLoadHandlerProxy: LazyAction<
    (onTabLoadedObj?: OnTabLoaded | undefined) => Promise<boolean>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyRegisterOnLoadHandler.importAndExecute);

export const lazyRegisterBeforeUnloadHandlerProxy: LazyAction<
    (onBeforeUnloadObj?: OnTabUnLoad | undefined) => Promise<void>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyRegisterBeforeUnloadHandler.importAndExecute);

export const lazyGetOwaDecodedAuthTokenFromMetaOsHubProxy: LazyAction<
    () => Promise<{
        exp: number;
        puid: string;
    }>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyGetOwaDecodedAuthTokenFromMetaOsHub.importAndExecute);

export const lazyPreInitOwaAccountSourceListStoreProxy: LazyAction<
    (
        puid?: string | undefined,
        tenantId?: string | undefined,
        userEmailAddress?: string | undefined,
        userPrincipalName?: string | undefined
    ) => Promise<string>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyPreInitOwaAccountSourceListStore.importAndExecute);

export const lazyGetAccountServiceConfigProxy: LazyAction<
    () => Promise<{
        getGlobalSettingsAccountMailboxInfo: () => MailboxInfo | undefined;
        getGlobalSettingsAccountPersistenceId: () => string | undefined;
    }>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyGetAccountServiceConfig.importAndExecute);

export const lazyNotifyAppLoadedProxy: LazyAction<
    () => Promise<void>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyNotifyAppLoaded.importAndExecute);

export const lazyIsNAAChannelRecommendedProxy: LazyAction<
    () => Promise<false> | Promise<true>,
    LazyBootModule<typeof lazyFunctions>
> = new LazyAction(lazyModule, m => m.lazyIsNAAChannelRecommended.importAndExecute);
